// Rendering and validation for a task's fulfillment form.
//
// The form is NOT hard-coded: the backend inlines it from
// service_fulfillment_type.structure, which is seeded per tenant. So the app
// does not know what fields it is about to render — everything here works off
// the definition it is handed.
//
// Extracted from TaskFulfillmentModal so the medication administration sheet
// can reuse it. The backend re-checks required fields on completion
// (fulfill_care_plan_task.py), so client and server must agree on what
// "required" and "empty" mean — that agreement lives in firstMissingRequired().
import React from "react";
import { MenuItem, TextField } from "@mui/material";

// structure is the whole {scheduling, fulfillment_form} object; the fields the
// nurse fills live under .fulfillment_form. Accept either shape (the raw
// structure or an already-unwrapped array) so a backend tweak to what
// nurse_dashboard_tasks returns doesn't break the form.
export function fieldsOf(fulfillmentForm) {
  if (Array.isArray(fulfillmentForm)) return fulfillmentForm;
  if (Array.isArray(fulfillmentForm?.fulfillment_form)) return fulfillmentForm.fulfillment_form;
  return [];
}

export function nowLocalInput(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Prettify a snake_case field name into a label.
export const labelize = (field) =>
  String(field).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function initialValues(fields) {
  const seed = {};
  fields.forEach((f) => {
    if (f.type === "object") {
      seed[f.field] = initialValues(f.structure || []);
    } else if (f.type === "datetime") {
      // Administration time defaults to now — the nurse is recording as they act.
      seed[f.field] = nowLocalInput();
    } else {
      seed[f.field] = "";
    }
  });
  return seed;
}

// Walk the field defs and collect the first required field left empty, so a
// caller can block submit with a specific message instead of a silent no-op.
export function firstMissingRequired(fields, values) {
  for (const f of fields) {
    if (f.type === "object") {
      const miss = firstMissingRequired(f.structure || [], (values || {})[f.field] || {});
      if (miss) return `${labelize(f.field)} — ${miss}`;
      continue;
    }
    if (f.required) {
      const v = (values || {})[f.field];
      if (v === "" || v === null || v === undefined) return labelize(f.field);
    }
  }
  return null;
}

// datetime-local yields "YYYY-MM-DDTHH:mm"; pad to seconds so the backend's
// parse_datetime accepts it. Leave everything else as typed.
export function serialize(fields, values) {
  const out = {};
  fields.forEach((f) => {
    const v = (values || {})[f.field];
    if (f.type === "object") {
      out[f.field] = serialize(f.structure || [], v || {});
    } else if (f.type === "datetime" && typeof v === "string" && v.length === 16) {
      out[f.field] = `${v}:00`;
    } else {
      out[f.field] = v;
    }
  });
  return out;
}

export function FieldInput({ def, value, onChange }) {
  const common = {
    size: "small",
    fullWidth: true,
    required: !!def.required,
    label: labelize(def.field),
    value: value ?? "",
    onChange: (e) => onChange(def.field, e.target.value),
  };
  if (def.type === "select") {
    return (
      <TextField select {...common}>
        {(def.options || []).map((opt) => (
          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
        ))}
      </TextField>
    );
  }
  if (def.type === "datetime") {
    return <TextField type="datetime-local" InputLabelProps={{ shrink: true }} {...common} />;
  }
  if (def.type === "date") {
    return <TextField type="date" InputLabelProps={{ shrink: true }} {...common} />;
  }
  if (def.type === "number") {
    return <TextField type="number" placeholder={def.placeholder || ""} {...common} />;
  }
  if (def.type === "textarea") {
    return <TextField multiline minRows={2} placeholder={def.placeholder || ""} {...common} />;
  }
  // string and any unknown type fall back to a text input.
  return <TextField placeholder={def.placeholder || ""} {...common} />;
}
